import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { ApexAxisChartSeries, ApexChart, ApexDataLabels, ApexFill, ApexMarkers, ApexStroke, ApexYAxis, ApexXAxis, ApexTooltip, ApexAnnotations, ApexLegend } from "ng-apexcharts";
import { FeUtil, TYPE } from "../../../hezky-routes";
import { ActivatedRoute } from "@angular/router";
import { AssetsService } from "../../../core/services/hezky-be-services/assets.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { DateTime } from "luxon";
import { finalize, take } from "rxjs";

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-storage-graph',
  templateUrl: './storage-graph.component.html',
})
export class StorageGraphComponent implements OnChanges {
  protected series!: ApexAxisChartSeries;
  protected chart!: ApexChart;
  protected dataLabels!: ApexDataLabels;
  protected markers!: ApexMarkers;
  protected fill!: ApexFill;
  protected stroke!: ApexStroke;
  protected yaxis!: ApexYAxis[];
  protected xaxis: ApexXAxis = { type: 'datetime', labels: { datetimeUTC: false } };
  protected annotations!: ApexAnnotations;
  protected legend!: ApexLegend;

  @Input({ required: true, alias: 'metaData' }) metaData!: TYPE.StorageMetaDataBase;
  @Input({ required: true }) viewOptions: TYPE.ViewOptions = {};
  @Input({ required: true }) capabilities: string[] = [];
  @Input({ required: true }) alarmData: any[] = [];

  @Input({ required: true }) isActive: boolean = true;
  @Input({ required: true }) decommissionDate: string = '';

  filterType: 'today' | 'yesterday' | 'week' | 'month'| '' = 'today';
  selectedSensorType: string = 'hot spot';
  sensorTypeOptions!: any[];
  graphData: any;
  lastReadingDataGrouped!: { [key: string]: any[] };
  loader: { [key: string]: boolean } = { graphLoader:false, logLoader:false, downloadLoader:false };
  dateRange!: string[];

  disableFutureDates = (current: Date): boolean => {
    if (this.decommissionDate && !this.isActive) {
      const decommission = DateTime.fromISO(this.decommissionDate).endOf('day');
      return DateTime.fromJSDate(current) > decommission;
    }
    return DateTime.fromJSDate(current) > DateTime.now();
  };

  constructor(public feUtil: FeUtil, public route: ActivatedRoute, private assetSvc: AssetsService) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.updateGraphAnnotation();
    if(this.graphData?.length) return;
    const isCapabilities = changes['capabilities'] && changes['capabilities'].currentValue && changes['capabilities'].currentValue.length;
    if (isCapabilities) {
      this.capabilities = this.capabilities.map((cap: string) => cap?.toLowerCase());
    }
    let filter: { filterType?: string; type: 'RADIO' | 'DATE-RANGE'; date?: string[] } | undefined;
    let startDate: string | null = null;
    let endDate: string | null = null;
    if (this.metaData.page === 'storage-incident') {
      startDate = this.metaData.date?.[0]
        ? DateTime.fromISO(this.metaData.date[0]).setZone(this.feUtil.time_zone).minus({ minutes: 30 }).toUTC().toISO()
        : null;
      endDate = this.metaData.date?.[1]
        ? DateTime.fromISO(this.metaData.date[1]).setZone(this.feUtil.time_zone).plus({ minutes: 30 }).toUTC().toISO()
        : DateTime.now().setZone(this.feUtil.time_zone).endOf('day').toUTC().toISO();
    }
    if (startDate && endDate) {
      filter = { type: 'DATE-RANGE', date: [startDate, endDate] };
    } else {
      filter = { filterType: this.filterType, type: 'RADIO' };
    }
    this.getGraphAndLogData(filter);
  }

  private getGraphAndLogData(filter?: { filterType?: string, type: 'RADIO' | 'DATE-RANGE', date?: string[] }): void {
    if (!this.metaData.id) return;
    this.loader['graphLoader'] = true;
    this.loader['logLoader'] = true;
    const fetchData$ = this.assetSvc.fetchGraphData({ idt_asset: this.metaData.id, timezone: this.feUtil?.nLevelPayload['timezone'] }, filter).pipe(untilDestroyed(this));
    fetchData$.pipe(untilDestroyed(this), take(1), finalize(() => {
      this.loader['graphLoader'] = false;
      this.loader['logLoader'] = false;
      this.updateGraphAnnotation();
    })).subscribe({
      next: ({ data }) => {
        this.graphData = this.assetSvc.getMatrixData(data).map((data) => {
          const combinedSeries = this.combineSeriesWithSameName(data.readingData.map(({ metaData, timeseriesData }) => ({
            color: metaData?.abbreviation?.color_code ? metaData?.abbreviation?.color_code[metaData?.matrixType?.toLowerCase()] : this.feUtil.getColor(metaData?.sensorType, metaData?.matrixType),
            name: metaData?.sensorType?.toUpperCase(),
            data: timeseriesData.map(({ datetime, value }: any) => ({ x: datetime, y: metaData?.matrixType === 'temperature' && this.feUtil.temp_pref === 'F' ? Number(value * 1.8 + 32).toFixed(1) : value }))
          })));
          const lastReadingData = this.feUtil.groupBySensorType(data.readingData.flatMap(d => d.timeseriesData));
          const sensorTypeOptions = Object.keys(lastReadingData);
          const selectedSensorType = sensorTypeOptions.includes('hot spot') ? 'hot spot' : sensorTypeOptions.includes('cold spot') ? 'cold spot' : sensorTypeOptions[0];
          return {
            matrixType: data.readingData[0]?.metaData?.matrixType,
            sensorTypeOptions,
            selectedSensorType,
            lastReadingData,
            chartOptions: {
              series: combinedSeries,
            },
          };
        });
        this.graphData = Array.isArray(this.graphData) && this.graphData.length ? this.graphData.filter((i: any) => this.capabilities.includes(i.matrixType)) : [];
        if(this.graphData?.length) this.initChartData();
      }
    });
  }

  private initChartData(): void {
    this.chart = {
      type: 'area',
      stacked: false,
      height: 355,
      zoom: { enabled: this.viewOptions.allowZoom ?? true },
      toolbar: { 
        show: true,
        autoSelected: 'zoom',
        tools: {
          download: this.viewOptions.showDownloadButton ?? true,
          selection: true,
          zoom: this.viewOptions.allowZoom ?? true,
          zoomin: this.viewOptions.allowZoom ?? true,
          zoomout: this.viewOptions.allowZoom ?? true,
          pan: true,
          reset: true
        }
      },
      animations: { enabled: false },
    };
    this.legend = {
      show: true,
      showForSingleSeries : true
    }
    this.dataLabels = { enabled: false };
    this.markers = { size: 0 };
    this.fill = {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        inverseColors: false,
        opacityFrom: 0.5,
        opacityTo: 0,
        stops: [0, 90, 100]
      }
    };
    this.yaxis = [{
      min: (min: number) => min - 0.75,
      max: (max: number) => max + 0.75,
      tickAmount: 5
    }];
    this.xaxis = {
      type: 'datetime',
      labels: {
        datetimeUTC: false,
        hideOverlappingLabels: true,
        rotate: 0,
        showDuplicates: false,
        trim:true,
        offsetX: 20,
        formatter: (value: string, timestamp?: number) => {
          return DateTime.fromMillis(timestamp ?? +value).setZone(this.feUtil.time_zone).toFormat('MMM d, y HH:mm');
        }
      },
      tooltip: {
        enabled: false,
      }
    };
  }

  protected onRadioChange(): void {
    this.getGraphAndLogData({ filterType: this.filterType, type: 'RADIO' });
    this.metaData.date = [];
  }

  protected onDateRangeChange(event: Date[]): void {
    if (!event || event.length !== 2) {
      this.filterType = 'today';
      this.getGraphAndLogData({ filterType: this.filterType, type: 'RADIO' });
      this.dateRange = [];
      return
    }
    this.filterType = '';
    let startDate = DateTime.fromJSDate(event[0]).setZone(this.feUtil.time_zone).startOf('day').toUTC().toISO() || '';
    let endDate = DateTime.fromJSDate(event[1]).setZone(this.feUtil.time_zone).endOf('day').toUTC().toISO() || '';
    this.dateRange = [startDate, endDate];
    this.getGraphAndLogData({ type: 'DATE-RANGE', date: [startDate, endDate] });
  }

  private combineSeriesWithSameName(series: any[]): any[] {
    // Use a more efficient data structure for large datasets
    const seriesMap = new Map<string, { name: string, data: any[] }>();
    // Pre-allocate arrays for better memory management
    series.forEach(s => {
      if (seriesMap.has(s.name)) {
        const existingSeries = seriesMap.get(s.name)!;
        // Use push instead of spread operator for better performance
        s.data.forEach((point: any) => existingSeries.data.push(point));
      } else {
        // Create a new array with initial capacity
        seriesMap.set(s.name, { 
          ...s,
          data: new Array(s.data.length)
        });
        // Copy data points
        s.data.forEach((point: any, i: number) => seriesMap.get(s.name)!.data[i] = point);
      }
    });
    // Use typed arrays for better performance with large datasets
    return Array.from(seriesMap.values()).map(s => {
      const sortedData = s.data.sort((a, b) => {
        // Cache timestamp values to avoid repeated conversions
        const timeA = a.x instanceof Date ? a.x.getTime() : new Date(a.x).getTime();
        const timeB = b.x instanceof Date ? b.x.getTime() : new Date(b.x).getTime();
        return timeA - timeB;
      });
      return {
        ...s,
        data: sortedData
      };
    });
  }

  private updateGraphAnnotation(): void {
    if (!Array.isArray(this.graphData) || this.graphData.length === 0) return;
    const matrixType = this.graphData[0]?.matrixType?.toLowerCase();
    if (!matrixType) return;
    const alarmMap: Record<string, any> = {};
    for (const d of this.alarmData) {
      const lowerKeys = Object.keys(d).reduce((acc, k) => {
        acc[k.toLowerCase()] = d[k];
        return acc;
      }, {} as any);
      for (const k in lowerKeys) {
        if (Object.prototype.hasOwnProperty.call(lowerKeys, k)) {
          alarmMap[k] = lowerKeys[k];
        }
      }
    }
    const matrixAlarm = alarmMap[matrixType];
    let annotationData: any = {};
    if (Array.isArray(matrixAlarm)) {
      const yaxisData = [];
      for (const alarmEntry of matrixAlarm) {
        if (Array.isArray(alarmEntry?.thresholds)) {
          for (const { value, severity, condition } of alarmEntry.thresholds) {
            const color =
              severity === 'High' ? '#e60707' :
              severity === 'Medium' ? '#FF8D0F' : '#eaaa08';
            yaxisData.push({
              y: matrixType === 'temperature' && this.feUtil.temp_pref === 'F' ? Number(value * 1.8 + 32).toFixed(1) : value,
              borderColor: color,
              borderWidth: 3,
              label: {
                text: `Severity: ${severity} | Condition: ${condition}`,
                style: {
                  color: "#fff",
                  fontSize: "9px",
                  background: color,
                }
              }
            });
          }
        }
      }
      if (yaxisData.length) {
        annotationData = { yaxis: yaxisData };
      }
    }
    if (annotationData.yaxis) {
      for (const item of this.graphData) {
        if (item?.chartOptions) item.chartOptions.annotations = annotationData;
      }
    }
  }

  protected onDownloadClick(): void {
    this.loader['downloadLoader'] = true;
    let payload: any = {
      idt_asset: this.metaData.id,
      timezone: this.feUtil?.nLevelPayload['timezone']
    }
    if (this.filterType) {
      const { date1, date2 } = this.assetSvc.getDatesByFilterType(this.filterType);
      payload['date1'] = date1;
      payload['date2'] = date2;
    }
    if (this.dateRange?.length) {
      payload['date1'] = this.dateRange[0];
      payload['date2'] = this.dateRange[1];
    }
    this.assetSvc.downloadExcel(payload).pipe(untilDestroyed(this)).subscribe({
      next: (res: any) => {
        if (res?.status === 200 && res?.fileUrl) {
          const link = document.createElement('a');
          link.href = res.fileUrl;
          link.download = '';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          console.error('Failed to download file: Invalid response', res);
        }
        this.loader['downloadLoader'] = false;
      },
      error: (err: any) => {
        console.log(err);
        this.loader['downloadLoader'] = false;
      }
    })
  }
}